import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { getVisitorToken } from '@/lib/visitor';
import type { Idea as IdeaType } from '@/hooks/useIdeas';
import Seo from '@/components/Seo';
import Starfield from '@/components/Starfield';
import { GrainOverlay, CustomCursor } from '@/components/Overlays';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import IdeaCard from '@/components/IdeaCard';

const IdeaPage = () => {
  const { id } = useParams<{ id: string }>();
  const [idea, setIdea] = useState<IdeaType | null>(null);
  const [voted, setVoted] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let active = true;
    (async () => {
      const { data } = await supabase
        .from('ideas')
        .select('id,title,body,author_name,vote_count,comment_count,created_at')
        .eq('id', id)
        .maybeSingle();
      if (!active) return;
      setIdea((data as IdeaType) ?? null);
      setLoading(false);
      const { data: votes } = await supabase
        .from('idea_votes')
        .select('id')
        .eq('idea_id', id)
        .eq('voter_token', getVisitorToken());
      if (active) setVoted((votes ?? []).length > 0);
    })();
    return () => { active = false; };
  }, [id]);

  const vote = async (ideaId: string) => {
    if (voted) return;
    setVoted(true);
    setIdea(prev => (prev ? { ...prev, vote_count: prev.vote_count + 1 } : prev));
    await supabase.from('idea_votes').insert({ idea_id: ideaId, voter_token: getVisitorToken() });
  };

  return (
    <>
      <Seo
        title={idea ? `${(idea.title || idea.body).slice(0, 80)} — DUMBVERSE` : 'Idea — DUMBVERSE'}
        description={idea ? idea.body.slice(0, 155) : 'An idea posted on Dumbverse.'}
        path={`/idea/${id ?? ''}`}
        image="https://dumbverse.app/og-image.png"
        type="article"
      />
      <Starfield />
      <GrainOverlay />
      <CustomCursor />
      <Navbar />
      <main className="pt-28 pb-16 px-6 min-h-screen">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="font-mono text-xs text-muted-foreground hover:text-foreground">
            ← Back to the feed
          </Link>
          <div className="mt-6">
            {loading ? (
              <p className="font-mono text-sm text-muted-foreground">Loading idea...</p>
            ) : !idea ? (
              <p className="font-mono text-sm text-muted-foreground">
                This idea drifted off into the void.
              </p>
            ) : (
              <IdeaCard idea={idea} voted={voted} onVote={vote} defaultOpen linkToPage={false} />
            )}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/#post"
              className="inline-block px-6 py-3 rounded bg-primary text-primary-foreground font-bebas text-lg tracking-wide hover:bg-primary/90 transition-colors"
            >
              Post your own idea
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default IdeaPage;
